import { getAsChoices, SubmissionFulfillmentType } from "../enums";
import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import {
  DateTimeField,
  LongTextField,
  NumberField,
  RelationshipField,
  SelectField,
  StringField,
} from "../plugins/Cruxtify/field-types";

import {
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import { ErCrudModel } from "./ErCrudModel";

export class Credit extends ErCrudModel {
  public static icon = "mdi-currency-usd";
  protected static _typeLabel = "Credit";
  protected static _typeLabelPlural = "Credits";
  protected static asProperty = "credit";
  public static api = {
    path: "credits",
  };
  protected static routeBase = "credits";
  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
    },
    UserPermission.Read
  );

  protected userIdField = ["user"];

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Purchased",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Entry Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "Entry Deleted At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "type",
        label: "Type",
        rules: ["required"],
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "note",
        label: "Note",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "stripe_transaction_id",
        label: "Transaction ID",
        computed: true,
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "user",
        label: "User",
        relatedModel: "User",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "submission",
        label: "Case Submission",
        relatedModel: "Submission",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    {
      type: DateTimeField,
      opts: { property: "created_at" },
    },
    { type: DateTimeField, opts: { property: "updated_at" } },
    { type: DateTimeField, opts: { property: "deleted_at" } },
    { type: LongTextField, opts: { property: "note" } },
    {
      type: SelectField,
      opts: {
        property: "type",
        cols: 4,
        options: getAsChoices(SubmissionFulfillmentType),
        rules: ["required"],
      },
    },
    {
      type: StringField,
      opts: {
        property: "stripe_transaction_id",
        cols: 4,
        viewComponentName: "stripe-transaction-link",
        label: "Transaction Details",
      },
    },
    {
      type: RelationshipField,
      opts: { property: "user", md: 6, isPrimaryLabelField: 1 },
    },
    {
      type: RelationshipField,
      opts: { property: "submission", md: 6, isPrimaryLabelField: 2 },
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      id: "Crudform",
      fieldSets: [
        {
          id: "related",
          fields: ["note", "user", "submission"],
        },
        {
          id: "meta",
          fields: ["stripe_transaction_id", "created_at", "updated_at", "id"],
        },
      ],
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      // This is the billing page's "Purchase History" table (User `billing`
      // layout -> `credits`), and it is SHARED with the admin credit list.
      //
      // `user` stays, and NOT for the reason it looks like. It never reaches
      // the billing page at all: the `credits` relation attaches a static
      // hidden filter on `user` (`RelationshipPropertyMany.collection`), and
      // `DataTableLayout.vue` drops any column whose id matches a static
      // filter's `queryName` — which resolves to `"user"`. So billing renders
      // four columns and the admin list renders five, from this one list.
      // It stays because the admin list does use it, and because dropping a
      // column from a shared layout is a content change, not a restyle.
      //
      // Because the two consumers render different column counts, no rule in
      // `assets/screens/_billing.scss` may key on column POSITION. Its <=820px
      // rule uses `:has(.field-property--submission)` instead — `Field.vue`
      // emits `field-property--<name>` inside each `<td>`, which is correct in
      // both consumers and survives reordering this list.
      columnFields: [
        "created_at",
        "type",
        "user",
        "submission",
        "stripe_transaction_id",
      ],
      // Emits `type--automated` / `type--custom` / `type--case-analysis` on
      // each `<tr>` (Cruxtify `DataTableRow.rowPropertyClasses`; an unset type
      // emits `type--undefined`). Purely a CSS hook: it is what lets the
      // billing mockup's per-service type tag pick the right tint from the
      // foundation's `.er-tag--*` variants, which have no other way to reach
      // the cell — nothing else in the DOM distinguishes a Standard row from a
      // White-Glove one. The alternative sanctioned door, a `viewComponentName`
      // on the `type` field, would be worse: that is a per-field setting on the
      // shared model, so it would restyle the admin list too.
      //
      // Additive — no data, column or permission changes. It is inert on the
      // admin list only because no selector outside `.er-billing` matches
      // `tr.type--*`; that is the invariant to preserve, since these are
      // generic class names now emitted app-wide.
      rowClassProperties: ["type"],
      enableSearch: false,
      useModal: true,
      enableDynamicFilters: true,
      dynamicFilterOptions: {
        filterFields: [
          "user",
          "note",
          "created_at",
          "updated_at",
          "user",
          "submission",
          "stripe_transaction_id",
        ],
      },
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
  ];
}
